import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, BarChart3, Bot, CheckCircle2, RotateCcw, Route, ShieldAlert, ShieldCheck } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import { SkeletonPage } from '../ui/kit';
import './RouteDispatch.css';

const LABELS = { route: 'Route', dispatch: 'Dispatch' };
const MODES = ['off', 'test', 'live'];
const money = cents => `$ ${(Number(cents || 0) / 100).toFixed(2)}`;
const metric = value => value == null ? '–' : Number(value).toLocaleString('de-DE', { maximumFractionDigits: 2 });
const weights = value => Object.entries(value || {}).map(([key, weight]) => `${key}: ${metric(weight)}`).join(' · ') || '–';
const instant = value => Number.isFinite(Number(value))
  ? new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(Number(value)))
  : '–';
const READINESS_MAX_AGE_MS = 2 * 60 * 1000;
const positiveRate = value => typeof value === 'number' && Number.isFinite(value) && value > 0;
const rateLabel = value => positiveRate(value)
  ? `${value.toLocaleString('de-DE', { maximumSignificantDigits: 8 })} USD / 1 Mio. Tokens`
  : 'Fehlt';

function readinessStatus(data, checkedAt, now) {
  if (!data || data.format !== 'voogo-dispatch-runtime-readiness-v1'
      || data.configuration_source !== 'current_process_environment'
      || data.provider !== 'openai' || data.model !== 'gpt-5.6-sol'
      || data.reasoning_effort !== 'medium' || !Number.isInteger(data.deadline_ms)
      || data.deadline_ms < 100 || data.deadline_ms > 20000
      || !Number.isInteger(data.max_concurrent) || data.max_concurrent < 20 || data.max_concurrent > 30
      || typeof data.credential_present !== 'boolean'
      || typeof data.cost_rates_configured !== 'boolean'
      || typeof data.configuration_ready !== 'boolean'
      || typeof data.provider_contact_tested !== 'boolean'
      || !data.runtime || typeof data.runtime.backend_version !== 'string'
      || typeof data.runtime.managed_authorized !== 'boolean') return 'unknown';
  const ratesPresent = positiveRate(data.input_usd_per_million) && positiveRate(data.output_usd_per_million);
  if (data.cost_rates_configured !== ratesPresent
      || data.configuration_ready !== (data.credential_present && ratesPresent)) return 'unknown';
  if (!Number.isFinite(checkedAt) || now < checkedAt || now - checkedAt > READINESS_MAX_AGE_MS
      || data.runtime.managed_authorized !== true
      || typeof data.runtime.generation !== 'string' || !data.runtime.generation
      || typeof data.runtime.manifest !== 'string' || !data.runtime.manifest) return 'unknown';
  return data.configuration_ready ? 'complete' : 'incomplete';
}

function DispatchReadiness({ data, checkedAt, now, refreshing, disabled, onRefresh }) {
  const status = readinessStatus(data, checkedAt, now);
  const recognized = data?.format === 'voogo-dispatch-runtime-readiness-v1';
  const stale = Number.isFinite(checkedAt) && now - checkedAt > READINESS_MAX_AGE_MS;
  const statusText = status === 'complete' ? 'Konfiguration vollständig'
    : status === 'incomplete' ? 'Konfiguration unvollständig'
      : stale ? 'Status veraltet – bitte aktualisieren' : 'Konfiguration nicht prüfbar';
  return (
    <section className={`rd-section rd-readiness is-${status}`} data-dispatch-readiness data-status={status}>
      <header>
        <div><Bot size={19} /><div><h2>Dispatch-KI: Konfiguration</h2><p>Lesender Status der aktiven Serverlaufzeit.</p></div></div>
        <button type="button" onClick={onRefresh} disabled={disabled || refreshing} aria-label="Dispatch-Status aktualisieren">
          <RotateCcw size={16} /> {refreshing ? 'Wird aktualisiert …' : 'Status aktualisieren'}
        </button>
      </header>
      <p className="rd-readiness-state" role="status">{statusText}</p>
      <div className="rd-readiness-values">
        <div><span>KI-Zugang</span><b>{recognized && typeof data?.credential_present === 'boolean'
          ? data.credential_present ? 'Vorhanden' : 'Fehlt' : 'Nicht prüfbar'}</b></div>
        <div><span>Eingabe-Kostensatz</span><b>{recognized ? rateLabel(data?.input_usd_per_million) : 'Nicht prüfbar'}</b></div>
        <div><span>Ausgabe-Kostensatz</span><b>{recognized ? rateLabel(data?.output_usd_per_million) : 'Nicht prüfbar'}</b></div>
        <div><span>Modell</span><b>{data?.format === 'voogo-dispatch-runtime-readiness-v1' && data.model === 'gpt-5.6-sol'
          ? 'gpt-5.6-sol · medium' : 'Nicht prüfbar'}</b></div>
      </div>
      <p className="rd-readiness-limit">{data?.provider_contact_tested === false
        ? 'KI-Verbindung noch nicht geprüft. Vollständige Konfiguration beweist keinen erfolgreichen Provideraufruf.'
        : 'KI-Verbindung durch diese Anzeige nicht belegt.'}</p>
      <details><summary>Technische Details</summary>
        <p>Gelesen: {checkedAt == null ? 'Noch nicht' : instant(checkedAt)} · Frist: {Number.isInteger(data?.deadline_ms) ? `${data.deadline_ms / 1000} s` : '–'} · Gleichzeitige Anfragen: {Number.isInteger(data?.max_concurrent) ? data.max_concurrent : '–'}</p>
        <p>Backend: {data?.runtime?.backend_version || '–'} · Generation: {data?.runtime?.generation || '–'}</p>
      </details>
    </section>
  );
}

function KpiSet({ title, data }) {
  const avg = data?.averages || {};
  return (
    <div className="rd-kpi-set">
      <strong>{title}</strong>
      <code>{data?.policy_version || 'Version unbekannt'}</code>
      <span>Ereignisse <b>{data?.event_count ?? data?.n ?? 0}</b> · Fahrten <b>{data?.ride_count ?? 0}</b> · Quotes <b>{data?.quote_count ?? 0}</b></span>
      <span>Modus live / test <b>{metric(data?.n_live)} / {metric(data?.n_test)}</b></span>
      <span>Quote-Parität <b>{metric(data?.price_quote_parity_pct)} %</b> (n={data?.price_parity_n ?? 0})</span>
      <span>ETA-Fehler Ø <b>{metric(avg.eta_error_pct)} %</b> (n={data?.counts?.eta_error_pct ?? 0})</span>
      <span>ETA-Fehler Median <b>{metric(data?.medians?.eta_error_pct)} % · {metric(data?.medians?.eta_error_min)} min</b></span>
      <span>ETA-Abweichung Ø <b>{metric(avg.eta_error_min)} min</b></span>
      <span>Datenqualität: ungültig {data?.quality?.invalid || 0} · synthetisch {data?.quality?.synthetic || 0} · unbekannt {data?.quality?.unknown || 0} · große Verzögerung {data?.quality?.large_delay || 0}</span>
      <span>Annahmezeit <b>{metric(avg.acceptance_time_sec)} s</b> (n={data?.counts?.acceptance_time_sec ?? 0})</span>
      <span>Leerstrecke (Luftlinie) <b>{metric(avg.empty_km)}</b> (n={data?.counts?.empty_km ?? 0})</span>
      <span>Fallback / Timeout <b>{metric(data?.fallback_rate_pct)} / {metric(data?.timeout_rate_pct)} %</b> (Provider-Versuche n={data?.provider_attempt_n ?? 0})</span>
      <span>Rückgabe / Storno / erfolglose Suche <b>{metric(avg.terminal_failure == null ? null : avg.terminal_failure * 100)} %</b> (n={data?.counts?.terminal_failure ?? 0})</span>
      <span>Latenz <b>{metric(avg.latency_ms)} ms</b></span>
    </div>
  );
}

function SystemCard({ control, kpis, busy, canApplyPricing, onMode, onRollback, onStop }) {
  const label = LABELS[control.system];
  return (
    <article className="rd-system" data-intelligence-system={control.system}>
      <header>
        <div><span className={`rd-state is-${control.mode}`}>{control.mode}</span><h2>{label}</h2><code>{control.system}_intelligence_mode</code></div>
        <span className="rd-revision">Revision {control.revision}</span>
      </header>
      <div className="rd-versions">
        <div><span>Aktiv</span><code>{control.active_policy_version}</code></div>
        <div><span>Vorherig</span><code>{control.previous_policy_version || '–'}</code></div>
        <div><span>Vorgeschlagen</span><code>{control.proposed_policy_version || '–'}</code></div>
      </div>
      <div className="rd-modes" aria-label={`${label} Betriebsmodus`}>
        {MODES.map(mode => (
          <button key={mode} type="button" disabled={busy || mode === control.mode || (mode !== 'off' && !canApplyPricing)}
            title={mode !== 'off' && !canApplyPricing ? 'Geschuetzter Owner fuer Candidate-Wirkung erforderlich' : undefined}
            className={mode === control.mode ? 'active' : ''} aria-label={`${label} Modus ${mode}`}
            onClick={() => onMode(control, mode)}>{mode}</button>
        ))}
      </div>
      <p>test wirkt global auf alle Fahrten. live verwendet denselben Candidate und dokumentiert die Owner-Freigabe.</p>
      <div className="rd-actions">
        <button type="button" disabled={busy || !canApplyPricing || (control.mode === 'off' && control.active_policy_version.includes('baseline'))}
          title={!canApplyPricing ? 'Geschuetzter Owner erforderlich' : undefined}
          aria-label={`${label} auf Baseline zurück`} onClick={() => onRollback(control)}><RotateCcw size={16} /> Auf Baseline zurück</button>
        <button type="button" className="danger" disabled={busy || !canApplyPricing}
          title={!canApplyPricing ? 'Geschuetzter Owner erforderlich; sichere Abschaltung bleibt ueber Modus off moeglich' : undefined}
          aria-label={`${label} Not-Aus`} onClick={() => onStop(control)}><ShieldAlert size={16} /> Not-Aus</button>
      </div>
      <p role="status">{kpis?.benefit?.status === 'descriptive_target_met'
        ? `Deskriptives Nutzenziel erreicht: ${metric(kpis.benefit.improvement_pct)} % Verbesserung. Owner-Prüfung erforderlich.`
        : kpis?.benefit?.status === 'target_not_met' ? `Nutzenziel nicht erreicht: ${metric(kpis.benefit.improvement_pct)} % Verbesserung.`
          : kpis?.benefit?.status === 'data_quality_review' ? 'Nutzen nicht belastbar: Datenqualität prüfen.'
            : `Nicht ausreichend belegt: mindestens ${kpis?.benefit?.minimum_sample || 30} Messungen je Kennzahl und Vergleichsversion erforderlich.`}</p>
      <p>Andere Policy-Versionen: {kpis?.excluded_version_event_count || 0} Ereignisse, separat vom Vergleich.</p>
      <div className="rd-kpis">
        <KpiSet title="Baseline" data={kpis?.baseline} />
        <KpiSet title="Candidate / neue Policy" data={kpis?.test} />
      </div>
    </article>
  );
}

function DispatchFallbacks({ data }) {
  if (!data) return (
    <section className="rd-section rd-fallbacks" data-dispatch-fallbacks>
      <header><div><AlertTriangle size={19} /><div><h2>Dispatch-Fallbackgründe</h2><p>Aufschlüsselung nicht verfügbar.</p></div></div></header>
    </section>
  );
  const population = data.population || {};
  const preProvider = data.pre_provider || {};
  return (
    <section className="rd-section rd-fallbacks" data-dispatch-fallbacks>
      <header><div><AlertTriangle size={19} /><div><h2>Dispatch-Fallbackgründe</h2><p>Gespeicherte Versuchsbindungen, ohne rohe Providerfehlermeldungen.</p></div></div></header>
      <div className="rd-fallback-meta">
        <span><b>Zeitraum</b>{instant(data.window?.from_ms)} bis {instant(data.window?.through_ms)} ({data.window?.timezone || 'Europe/Berlin'})</span>
        <span><b>Grundgesamtheit</b>{population.bound_attempts_total ?? 0} persistierte Dispatch-Versuchsbindungen</span>
        <span><b>Ergebnis</b>{population.completed_results_total ?? 0} vollständig · {population.fallback_results_total ?? 0} Fallback · {population.open_or_other_total ?? 0} offen/sonstig</span>
      </div>
      {data.timeout_alert?.active && <p role="alert">
        Dispatch-Zeitlimits auffällig: {data.timeout_alert.timeouts} von {data.timeout_alert.bound_attempts} gebundenen Versuchen in den letzten 24 Stunden. Die Baseline übernimmt; Modellantworten werden nicht als erfolgreich gezählt.
      </p>}
      <div className="rd-fallback-models" aria-label="Modelle der gebundenen Versuche">
        {(data.models || []).map(model => (
          <code key={`${model.provider}/${model.model}`}>{model.provider}/{model.model}: {model.bound_attempts} gebundene Versuche · {model.fallbacks} Fallbacks</code>
        ))}
        {!data.models?.length && <span>Im Zeitraum ist kein gebundener Versuch gespeichert.</span>}
      </div>
      <div className="rd-fallback-reasons">
        {(data.reasons || []).map(reason => (
          <div key={reason.code} className={reason.known === false ? 'is-unknown' : ''}>
            <span>{reason.label}</span><b>{reason.count}</b>
          </div>
        ))}
        {!data.reasons?.length && <p>Keine gespeicherten Providerfallbacks im Zeitraum.</p>}
      </div>
      <p className="rd-measurement-limit">
        Transportstart bei offenen oder vom Reaper geschlossenen Versuchen nicht sicher belegbar: <b>{population.transport_start_uncertain_total ?? 0}</b>.
        Nach Reservierung, aber ohne Versuchsbindung: <b>{preProvider.persisted_without_attempt_binding_total ?? 0}</b>.
        {(preProvider.persisted_reasons || []).map(reason => ` ${reason.label}: ${reason.count}.`).join('')}
        Vorprüfungen vor dem Persistieren werden technisch nicht gezählt; dafür existiert bewusst keine geschätzte Gesamtzahl.
      </p>
    </section>
  );
}


// ROUTENWAHL-ZEITWERT-001: Zeitwert Z der Routenwahl. Jede angebotene Route wird
// mit voller Stauzeit bepreist; gewaehlt wird die kleinste Punktzahl
// Fahrpreis + Z x Mehrminuten gegenueber der schnellsten Route. Preiswirkung -> Owner-only.
const zText = value => Number.isFinite(Number(value)) ? Number(value).toFixed(2).replace('.', ',') : '';
function RouteTimeValue({ data, busy, canApplyPricing, onSave }) {
  const [draft, setDraft] = useState(() => zText(data?.eur_per_min));
  useEffect(() => { setDraft(zText(data?.eur_per_min)); }, [data?.eur_per_min]);
  if (!data || typeof data !== 'object') return null;
  const dirty = draft.trim() !== zText(data.eur_per_min);
  const disabled = busy || !canApplyPricing;
  return (
    <section className="rd-section rd-time-value" data-route-time-value data-stored={data.stored}>
      <header><div><Route size={19} /><div><h2>Routenwahl: Zeitwert Z</h2>
        <p>Alle angebotenen Routen werden mit voller Stauzeit bepreist. Gewinner: kleinste Punktzahl = Fahrpreis + Z × Mehrminuten gegenüber der schnellsten Route. Bezahlt wird der echte Fahrpreis der gewählten Route.</p></div></div></header>
      <div className="rd-time-value-row">
        <label>Z in € je Mehrminute
          <input type="text" inputMode="decimal" value={draft} disabled={disabled} aria-label="Zeitwert Z in Euro je Mehrminute"
            onChange={event => setDraft(event.target.value)} />
        </label>
        <button type="button" disabled={disabled || !dirty} onClick={() => onSave(draft)}
          title={!canApplyPricing ? 'Geschuetzter Owner erforderlich' : undefined}>Speichern</button>
        <span className="rd-time-value-meta">
          Wirksam: <b>{zText(data.eur_per_min)} €</b> · Werkswert {zText(data.default_eur_per_min)} € · erlaubt {zText(data.min_eur_per_min)}–{zText(data.max_eur_per_min)} €
          {data.updated_at ? ` · zuletzt geändert ${data.updated_at}` : ''}
        </span>
      </div>
      {data.stored === 'invalid_uses_default' && <p className="rd-time-value-warning" role="alert">Gespeicherter Wert ungültig, es gilt der Werkswert. Bitte neu speichern.</p>}
      <p className="rd-time-value-hint">Z = 0: immer die billigste Route. Z = 0,15: eine Route darf pro Mehrminute 15 Cent teurer sein, dann lohnt sich die schnellere. Ab etwa 0,30 kippen die meisten Fahrten über 10 km zur Autobahn. Beispiel Stadt 5,1 km/18 min = 9,64 € gegen Autobahn 10 km/12 min = 11,96 €: bei Z = 0,15 gewinnt die Stadt (9,64 + 6 × 0,15 = 10,54).</p>
    </section>
  );
}

// STAU-SATZ-001 (Owner 14.10.2026): EIN Stausatz für Ultimate/PULSE Normal. Stau = Fahrzeit jetzt − Fahrzeit derselben
// Route um 3 Uhr nachts. Stauminuten kosten diesen Satz statt des Abschnittssatzes (vor allen Faktoren),
// höchstens bis zur Obergrenze. 0 schaltet den Stausatz aus. Preiswirkung -> Owner-only.
const capText = value => Number.isInteger(Number(value)) ? String(Number(value)) : '';
function StauRate({ data, busy, canApplyPricing, onSave }) {
  const [rate, setRate] = useState(() => zText(data?.eur_per_min));
  const [cap, setCap] = useState(() => capText(data?.cap_min));
  useEffect(() => { setRate(zText(data?.eur_per_min)); }, [data?.eur_per_min]);
  useEffect(() => { setCap(capText(data?.cap_min)); }, [data?.cap_min]);
  if (!data || typeof data !== 'object') return null;
  const rateDirty = rate.trim() !== zText(data.eur_per_min);
  const capDirty = cap.trim() !== capText(data.cap_min);
  const disabled = busy || !canApplyPricing;
  const invalid = data.stored && (data.stored.rate === 'invalid_uses_default' || data.stored.cap === 'invalid_uses_default');
  return (
    <section className="rd-section rd-time-value" data-stau-rate data-active={data.active ? 'true' : 'false'}>
      <header><div><Route size={19} /><div><h2>Stausatz</h2>
        <p>Stau = Fahrzeit jetzt minus Fahrzeit derselben Route um 3 Uhr nachts. Stauminuten kosten diesen festen Satz statt des Minutensatzes der Strecke, für alle betroffenen Strecken gleich und vor allen Faktoren. Gilt für Ultimate und PULSE Normal. Der PULSE-Essen-Tarif bleibt unverändert.</p></div></div></header>
      <div className="rd-time-value-row">
        <label>Stausatz in € je Stauminute
          <input type="text" inputMode="decimal" value={rate} disabled={disabled} aria-label="Stausatz in Euro je Stauminute"
            onChange={event => setRate(event.target.value)} />
        </label>
        <label>Höchstens Stauminuten
          <input type="text" inputMode="numeric" value={cap} disabled={disabled} aria-label="Obergrenze der Stauminuten"
            onChange={event => setCap(event.target.value)} />
        </label>
        <button type="button" disabled={disabled || (!rateDirty && !capDirty)}
          onClick={() => onSave({ ...(rateDirty ? { eur_per_min: rate } : {}), ...(capDirty ? { cap_min: cap } : {}) })}
          title={!canApplyPricing ? 'Geschuetzter Owner erforderlich' : undefined}>Speichern</button>
        <span className="rd-time-value-meta">
          {data.active ? <>Aktiv: <b>{zText(data.eur_per_min)} €</b> je Stauminute, höchstens <b>{capText(data.cap_min)} min</b></> : <b>Aus</b>}
          {' '}· Werkswert {zText(data.default_eur_per_min)} € / {capText(data.default_cap_min)} min
        </span>
      </div>
      {invalid && <p className="rd-time-value-warning" role="alert">Gespeicherter Wert ungültig, es gilt der Werkswert. Bitte neu speichern.</p>}
      <p className="rd-time-value-hint">Beispiel: 7 km, 20 min, nachts 5 min → 15 Stauminuten kosten je 0,25 € statt 0,10 €. Satz 0 = aus (Preise wie ohne Stausatz).</p>
    </section>
  );
}

export default function RouteDispatch() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const load = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const response = await api.get('/admin/route-dispatch');
      const readAt = Date.now();
      setState(response.data); setCheckedAt(readAt); setNow(readAt); setLoadFailed(false);
    } catch (_) {
      setState(null); setCheckedAt(null); setLoadFailed(true);
      toast.error('Route & Dispatch konnte nicht geladen werden');
    } finally { if (manual) setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const act = async (request, success) => {
    setBusy(true);
    try {
      const response = await request();
      setState(response.data);
      toast.success(typeof success === 'function' ? success(response.data) : success);
    } catch (error) {
      const data = error?.response?.data;
      toast.error(data?.result?.reason || data?.error || 'Aktion fehlgeschlagen');
      // Nach jedem Fehler (insb. revision_conflict) frisch laden: Folgeaktionen — auch der
      // Not-Aus — duerfen nie auf einer veralteten Revision festhaengen.
      await load();
    } finally { setBusy(false); }
  };
  const onMode = (control, mode) => {
    if (mode === 'live' && !window.confirm(`${LABELS[control.system]} wirklich live schalten? Ohne belastbaren Nutzen kein Live-GO.`)) return;
    act(() => api.put(`/admin/route-dispatch/${control.system}/mode`, {
      mode, expected_revision: control.revision, confirm_live: mode === 'live',
    }), `${LABELS[control.system]} steht auf ${mode}`);
  };
  const onRollback = control => {
    if (!window.confirm(`${LABELS[control.system]} atomar auf die heutige Baseline zurücksetzen? Laufende Fahrten bleiben unverändert.`)) return;
    act(() => api.post(`/admin/route-dispatch/${control.system}/rollback`, { expected_revision: control.revision, confirm: true }), 'Baseline ist effektiv');
  };
  const onStop = control => {
    if (!window.confirm(`Not-Aus für ${LABELS[control.system]} bestätigen? Stoppt unbedingt und löscht den stehenden Vorschlag.`)) return;
    // Bewusst ohne expected_revision: der Not-Aus ist idempotent Richtung off/Baseline
    // und darf nicht an einem Revisionskonflikt scheitern.
    act(() => api.post(`/admin/route-dispatch/${control.system}/emergency-stop`, { confirm: true }), 'Not-Aus: Baseline ist effektiv');
  };
  const review = (recommendation, decision) => {
    if (decision === 'accept' && !window.confirm('Vorschlag annehmen? Die Gewichte werden nur vorgemerkt und erst durch eine ausdrückliche Modus-Aktion aktiv.')) return;
    const control = state.controls.find(c => c.system === recommendation.system);
    act(() => api.post(`/admin/route-dispatch/recommendations/${recommendation.id}/review`, {
      decision, expected_control_revision: control?.revision,
    }), decision === 'accept' ? 'Vorschlag vorgemerkt — aktiv erst nach Modus-Aktion' : 'Vorschlag abgelehnt');
  };
  const solRun = (retry = false) => act(() => api.post('/admin/route-dispatch/sol/run', retry ? { retry: true } : {}), data => {
    const status = data?.result?.status;
    if (status === 'completed') return 'Wochenlauf abgeschlossen';
    if (status === 'skipped') return 'Übersprungen: beide Modi stehen auf off';
    if (data?.result?.cached) return `Lauf dieser Woche existiert bereits (${status})`;
    return `Wochenlauf: ${status || 'unbekannt'}`;
  });
  const lastRun = state?.weekly_runs?.[0];

  if (!state) {
    return loadFailed
      ? (
        <main className="rd-page">
          <div className="rd-empty">
            Route &amp; Dispatch konnte nicht geladen werden.
            <button type="button" onClick={() => load()}>Erneut laden</button>
          </div>
        </main>
      )
      : <SkeletonPage />;
  }
  const canApplyPricing = state.can_apply_pricing === true;
  return (
    <main className="rd-page">
      <PageHeader
        title="Route & Dispatch"
        description="Versionierte Auswahl mit Baseline, gemessener Evidenz und sofortigem Rollback."
        actions={(
          <>
            <span className="rd-role"><ShieldCheck size={16} /> {canApplyPricing ? 'Geschuetzter Owner · Preiswirkung' : 'Manager · sichere Abschaltung'}</span>
            <div className="rd-provider"><b>Feste Rollen</b><span>Route: lokal · Dispatch: KI · Wochenbericht: Sol</span></div>
          </>
        )}
      />

      <DispatchReadiness data={state.dispatch_runtime_readiness} checkedAt={checkedAt} now={now}
        refreshing={refreshing} disabled={busy} onRefresh={() => load(true)} />

      <RouteTimeValue data={state.route_time_value} busy={busy || refreshing} canApplyPricing={canApplyPricing}
        onSave={value => act(() => api.put('/admin/route-dispatch/route/time-value', { eur_per_min: value }),
          data => `Zeitwert Z steht auf ${zText(data?.route_time_value?.eur_per_min)} € je Mehrminute`)} />

      <StauRate data={state.stau_rate} busy={busy || refreshing} canApplyPricing={canApplyPricing}
        onSave={values => act(() => api.put('/admin/route-dispatch/route/stau', values),
          data => data?.stau_rate?.active
            ? `Stausatz steht auf ${zText(data.stau_rate.eur_per_min)} € je Stauminute, höchstens ${capText(data.stau_rate.cap_min)} min`
            : 'Stausatz ist aus')} />

      <section className="rd-costs">
        <div><span>Luna gebucht</span><b>{money(state.costs?.luna_booked_cents)}</b></div>
        <div><span>Davon mit Tokenbeleg</span><b>{money(state.costs?.luna_usage_booked_cents)}</b></div>
        <div><span>Davon ohne Tokenbeleg (vorsorglich gebunden)</span><b>{money(state.costs?.luna_without_usage_booked_cents)}</b></div>
        <div><span>Luna offene Reserven</span><b>{money(state.costs?.luna_reserved_cents)}</b></div>
        <div><span>Sol (Monat, inkl. Reserven)</span><b>{money(state.costs?.sol_month_cents)}</b></div>
        <div><span>Sol ohne Tokenbeleg (vorsorglich gebunden)</span><b>{money(state.costs?.sol_without_usage_booked_cents)}</b></div>
        <div><span>Gesamt gebunden</span><b>{money(state.costs?.total_committed_cents)}</b></div>
        <div><span>Warnung</span><b>{money(state.costs?.warning_cents)}</b></div>
        <div><span>Gemeinsames Monatslimit</span><b>{money(state.costs?.hard_cents)}</b> <i>(Stufe: {state.costs?.assumed_daily_bookings ?? '–'} Fahrten/Tag)</i></div>
      </section>

      <DispatchFallbacks data={state.dispatch_fallbacks} />

      <p>Vergleich: aufbewahrte Ereignisse, getrennte Policy-Versionen. Sol nutzt nur lernberechtigte Daten der letzten 7 Tage. Erfolglose Suchen zählen im Fehleranteil; Annahmezeit und Luftlinie haben eigene Messzahlen. Kennzahlen sind deskriptiv; sie belegen keine Ursache.</p>
      <section className="rd-systems">
        {state.controls.map(control => <SystemCard key={control.system} control={control} kpis={state.kpis?.[control.system]}
          busy={busy || refreshing} canApplyPricing={canApplyPricing} onMode={onMode} onRollback={onRollback} onStop={onStop} />)}
      </section>

      <section className="rd-section">
        <header><div><Bot size={19} /><div><h2>Sol-Wochenbericht</h2><p>Aggregierte Analyse; niemals Auto-Apply — Annahme merkt nur vor.</p></div></div>
          <button type="button" disabled={busy} onClick={() => solRun(false)}>Jetzt analysieren</button></header>
        {lastRun?.status === 'failed' && (
          <div className="rd-empty">
            Letzter Lauf ({lastRun.week_key}) fehlgeschlagen: <b>{lastRun.failure_reason || 'unbekannt'}</b>
            <button type="button" disabled={busy} onClick={() => solRun(true)}>Erneut analysieren</button>
          </div>
        )}
        {!state.recommendations?.length && <div className="rd-empty">Noch keine Empfehlung. Ohne Datenvorteil bleibt die Baseline.</div>}
        {state.recommendations?.map(rec => (
          <article className="rd-recommendation" key={rec.id}>
            <div><span className={`rd-state is-${rec.status}`}>{rec.status}</span><h3>{LABELS[rec.system] || 'Ultimate Analyse'}</h3></div>
            <p><b>Erwartete Wirkung:</b> {metric(rec.recommendation?.expected_impact_pct)} % · <b>Konfidenz:</b> {metric((rec.recommendation?.confidence || 0) * 100)} %</p>
            <p><b>Begründung:</b> {rec.recommendation?.rationale || '–'}</p>
            <p><b>Gewichte aktuell:</b> {weights(rec.recommendation?.current_weights)}</p>
            <p><b>Gewichte vorgeschlagen:</b> {weights(rec.recommendation?.proposed_weights)}</p>
            <p><b>Datenbasis:</b> {rec.recommendation?.data_basis || '–'}</p>
            <p><b>Risiken:</b> {(rec.recommendation?.risks || []).join(', ') || '–'} · <b>Rollback:</b> {rec.recommendation?.rollback || 'Baseline'}</p>
            {rec.status === 'proposed' && <div className="rd-actions"><button onClick={() => review(rec, 'accept')} disabled={busy || !canApplyPricing}
              title={!canApplyPricing ? 'Geschuetzter Owner zum Vormerken erforderlich' : undefined}><CheckCircle2 size={16} /> Annehmen</button><button onClick={() => review(rec, 'reject')} disabled={busy || !canApplyPricing}
              title={!canApplyPricing ? 'Geschuetzter Owner zum Ablehnen erforderlich' : undefined}>Ablehnen</button></div>}
          </article>
        ))}
      </section>

      <section className="rd-section rd-guide">
        <header><div><BarChart3 size={19} /><div><h2>Betriebsanleitung</h2><p>Autoritäten, Nutzen, Kosten und Ausfallverhalten.</p></div></div></header>
        {Object.entries(state.instructions || {}).map(([group, entries]) => <div key={group}><h3>{group}</h3><ul>{entries.map(entry => <li key={entry}>{entry}</li>)}</ul></div>)}
        <aside><AlertTriangle size={18} /> Live ist kein Ergebnis dieses Reiters: Es erfordert eine separate Owner-Entscheidung nach belastbarem Nutzenbeleg.</aside>
      </section>
    </main>
  );
}
